import React, { useEffect, useState, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';
import { PartnerService, Partner, PartnerWithTransactions } from '../services/PartnerService';
import { EditPartner } from './EditPartner';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAuth } from '../contexts/AuthContext';
import './PartnerList.css';

const PartnerList: React.FC = () => {
    const { t } = useTranslation();
    const { isAdmin } = useAuth();
    const toast = useRef<Toast>(null);
    const [partners, setPartners] = useState<Partner[]>([]);
    const [selectedPartner, setSelectedPartner] = useState<Partial<PartnerWithTransactions>>({});
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [globalFilter, setGlobalFilter] = useState<string | null>(null);

    const fetchPartners = async () => {
        setLoading(true);
        try {
            const data = await PartnerService.fetchPartners();
            let loadedPartners: Partner[] = data;
            if ( isAdmin === false ) {
                loadedPartners = [];
                for (const p in data ) {
                    loadedPartners.push( await PartnerService.getPartnerById(data[p].id!) );
                }
            }

            setPartners(loadedPartners);
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: t('partnerList.fetchError'), detail: error.message, life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPartners();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpen = async (partner: Partial<PartnerWithTransactions>) => {
        try {
            const data = await PartnerService.getPartnerById(partner.id!);
            setSelectedPartner(data);
            setOpen(true);
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: t('partnerList.fetchError'), detail: error.message, life: 3000 });
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = async (partner: Partner) => {
        try {
            if (partner.id) {
                await PartnerService.editPartner(partner.id, partner);
            } else {
                await PartnerService.createPartner(partner);
            }
            fetchPartners();
            setOpen(false);
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: t('partnerList.saveError'), detail: error.message, life: 3000 });
        }
    };

    const handleAdd = () => {
        setSelectedPartner({});
        setOpen(true);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
        setGlobalFilter(event.target.value);
    };

    const renderHeader = () => {
        return (
            <div className="flex justify-content-between">
                <InputText value={searchText} onChange={handleSearch} placeholder={t('partnerList.searchLabel')} />
            </div>
        );
    };

    return (
        <div className="p-grid p-justify-center">
            <Toast ref={toast} />
            <div className="p-col-12 p-md-10">
                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ height: 'calc(100vh - 200px)' }}>
                        <ProgressSpinner />
                    </div>
                ) :
                    isAdmin ?
                        (
                            <>
                                <DataTable
                                    value={partners}
                                    globalFilter={globalFilter}
                                    header={renderHeader()}
                                    paginator
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 20]}
                                    emptyMessage={t('partnerList.noPartners')}
                                >
                                    <Column field="name" header={t('partnerList.name')} sortable />
                                    <Column field="email" header={t('partnerList.email')} sortable />
                                    <Column field="telephone" header={t('partnerList.telephone')} sortable />
                                    <Column field="numberOfTokens" header={t('partnerList.numberOfTokens')} sortable />
                                    <Column
                                        header={t('partnerList.actions')}
                                        body={(rowData: Partner) => (
                                            <div className="action-buttons" style={{ whiteSpace: 'nowrap' }}>
                                                <span className="pi pi-pencil" onClick={() => handleOpen(rowData)}></span>
                                            </div>
                                        )}
                                    />
                                </DataTable>
                                <Button
                                    icon="pi pi-plus"
                                    className="p-button-rounded"
                                    style={{ position: 'fixed', bottom: 16, right: 16 }}
                                    onClick={handleAdd}
                                />
                                <Dialog header={t('editPartner.title')} visible={open} dismissableMask={true} style={{ width: '80vw' }} onHide={handleClose}>
                                    <EditPartner partner={selectedPartner} onSave={handleSave} />
                                </Dialog>
                            </>
                        ) : (
                            partners.map(p => (<EditPartner partner={p} onSave={handleSave} />))
                        )
                }
            </div>
        </div>
    );
};

export default PartnerList;
